import { ProtectedEntity } from '@nirvana/shared/dtos/types/protected-entities/common';
import { ProtectedEntitiesHelper } from '../../helpers/protectedEntities/protected.entities.helper';
import { Feature } from '@nirvana/shared/constants';
import { ServiceTarget as ServiceTargetAuth, UserTarget } from '@nirvana/shared/context/nestjs';

export abstract class BaseNetworkActionValidator {
  private itemsCount = 0;
  private visitedCompaniesIds: Array<string> = [];

  protected canRunForMultipleItems = true;
  protected canRunOnMultipleCompanies = true;
  protected canRunOnCompanionRecords = true;

  /**
   * Tasks button is disabled when:
   * entity is container;
   * entity has mutual auth rejected && mutual auth enforced on company;
   * entity is Partner, Company Folder or Company Collection;
   *
   * @type {boolean}
   * @protected
   * @abstract
   */
  protected abstract requiresTasksEnabled: boolean;
  protected abstract canRunForUnmanagedCompanies: boolean;

  protected abstract _canPerformAction(record: ProtectedEntity): boolean;

  public canPerformAction(record: ProtectedEntity): boolean {
    this.itemsCount++;

    if (!this.canRunForMultipleItems && this.itemsCount > 1) {
      return false;
    }

    this.markCompanyVisited(record);

    if (!this.canRunOnMultipleCompanies && this.visitedCompaniesIds.length > 1) {
      return false;
    }

    if (!this.canRunForUnmanagedCompanies) {
      if (this.isReadOnlyChildCompany(record, Application.Main.user.company.id)) {
        return false;
      }
    }

    if (this.requiresTasksEnabled && !this.isTasksEnabled(record)) {
      return false;
    }

    if (!this.canRunOnCompanionRecords && ProtectedEntitiesHelper.isCompanion(record)) {
      return false;
    }

    return this._canPerformAction(record);
  }

  private markCompanyVisited(record: ProtectedEntity): void {
    const recordCompanyId = record.company?.id;

    if (!recordCompanyId) {
      // root node from tree
      if (record.type === Constants.Companies.TYPE_COMPANY) {
        return;
      }

      console.error(`Record ${record._id} has no company id`);
      return;
    }

    if (this.visitedCompaniesIds.includes(recordCompanyId)) {
      return;
    }

    this.visitedCompaniesIds.push(recordCompanyId);
  }

  /**
   * This function should only be called from BulkNetworkActionValidator or a class that extend it.
   * @param records
   */
  public canPerformBulkAction(records: Array<ProtectedEntity>): boolean {
    throw Error('Should be implemented in child class.');
  }

  /**
   * This function determines if an action should be validated in bulk or one by one.
   */
  public shouldValidateInBulk(): boolean {
    return false;
  }

  /**
   * if company && the company is not the user's top company
   * (user.companyId !== record.id) and it cannot be managed by above -> false
   */
  protected isReadOnlyChildCompany(record: ProtectedEntity, userCompanyId: string): boolean {
    if (!ProtectedEntitiesHelper.isCompany(record)) {
      return false;
    }

    const userOwnCompany: boolean = userCompanyId === record._id;

    return !userOwnCompany && !record.specifics.canBeManagedByAbove;
  }

  /**
   * is entity is container -> false
   * if entity has mutual auth rejected && mutual auth enforced on company -> false
   * if entity is [partner, company folder, company collection] -> false
   */
  protected isTasksEnabled(record: ProtectedEntity): boolean {
    if (ProtectedEntitiesHelper.isContainerType(record)) {
      return false;
    }

    if (ProtectedEntitiesHelper.hasMutualAuthRejected(record) && this.isMutualAuthEnforced()) {
      return false;
    }

    if (ProtectedEntitiesHelper.isPartner(record)) {
      return false;
    }

    return !ProtectedEntitiesHelper.isCompanyFolderOrCompanyCollection(record);
  }

  private isMutualAuthEnforced(): boolean {
    return Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_MUTUAL_AUTH_ENFORCED]);
  }

  protected isRecordInTargets(record: ProtectedEntity): boolean {
    const targetIds: Array<string> = Application.Main.getUser()
      .serviceTargets.map((serviceTarget: ServiceTargetAuth) => {
        return serviceTarget.targets.map((target: UserTarget) => {
          return target.protectedEntityId;
        });
      })
      .reduce((result, currentValue) => result.concat(currentValue), []);

    return targetIds.includes(record._id);
  }
}
